import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {OidcConfigProvider} from '@oidc/OidcConfigProvider';
import {BehaviorSubject, Observable} from 'rxjs';
import {UserData} from '../core/user/models/user-data';
import {AwpClientLibService} from '../services/awp-client-lib.service';
import {Util} from '../util/util';
import {AwpCookieService} from './awp-cookie.service';
import {CsslToken} from './cssl/cssl-token';
import {OidcToken} from './oidc/oidc-token';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationTokenBearerService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly libService: AwpClientLibService = inject(AwpClientLibService);
  private readonly awpCookieService: AwpCookieService = inject(AwpCookieService);

  public readonly oidcTokenSubject: BehaviorSubject<OidcToken>;
  public readonly csslTokenSubject: BehaviorSubject<CsslToken>;
  private csslTokenUserBusinessPartnerId: string | null = null;
  private csslTokenRefreshTimer;
  private csslTokenRefreshTimeoutMs: number = 2000;
  private csslTokenNextCheckDate: Date;
  /** Tracks whether the initial cookie create has already been attempted in this runtime. */
  private hasSyncedAwpCookie: boolean = false;

  constructor() {
    // In case tokens already exist
    const oidcToken = OidcToken.readFromStorage(this.resolveBusinessContext());
    this.oidcTokenSubject = new BehaviorSubject(oidcToken);
    this.csslTokenSubject = new BehaviorSubject(CsslToken.readFromStorage(oidcToken.business_context));

    // Wait for tokens to be set initially or refreshed
    window.addEventListener('oidcTokenRefreshed', (): void => {
      const previousToken: OidcToken = this.oidcTokenSubject.getValue();
      const currentToken: OidcToken = OidcToken.readFromStorage(this.resolveBusinessContext(previousToken));
      this.oidcTokenSubject.next(currentToken);
      this.libService.serverApi.publishOidcToken(currentToken.toLibToken());
      void this.syncAwpCookieWithOidcToken(previousToken, currentToken);
    });
  }

  /**
   * Resolves the business context to use for reading the OIDC token from storage. Prefers:
   * 1. The business context already known from a previously loaded token (e.g. set by the
   *    backend after login).
   * 2. The authenticated user's business context from the global `bmwNamespace.userData` object,
   *    which OidcFlow.v2.js populates during its pre-Angular bootstrap (see `handleUserData()`).
   *    This is the only reliable source once realms have fully migrated to Alpha, since Alpha
   *    realm hosts no longer carry a static `businessContext` in `OidcConfigProvider` (it is
   *    fully User API-driven, see ISPI-484050).
   * 3. The business context statically configured for the current hostname in
   *    `OidcConfigProvider`, which is still present for non-Alpha B2D/B2D_INTERNAL hosts and
   *    known synchronously, even before login / before any token or user data exists.
   */
  private resolveBusinessContext(knownToken?: OidcToken): string {
    return knownToken?.business_context
      || window['bmwNamespace']?.userData?.businessContext
      || (new OidcConfigProvider()).get(window.location.hostname)?.businessContext;
  }

  /**
   * Ensures initial cookie synchronization happens once after user bootstrap.
   */
  async ensureInitialAwpCookieSync(): Promise<void> {
    if (this.hasSyncedAwpCookie) {
      console.debug(this.logPrefix() + 'Skipping initial awp_cookie sync because it already ran.');
      return;
    }

    const currentToken: OidcToken = this.oidcTokenSubject.getValue();
    await this.syncAwpCookieWithOidcToken(new OidcToken(), currentToken);
  }

  /**
   * Synchronizes awp_cookie for login and token refresh events.
   */
  private async syncAwpCookieWithOidcToken(previousToken: OidcToken, currentToken: OidcToken): Promise<void> {
    const currentAccessToken: string = currentToken?.access_token;
    if (!currentAccessToken) {
      console.debug(this.logPrefix() + 'Skipping awp_cookie sync because no current OIDC access token is available.');
      return;
    }

    const previousAccessToken: string = previousToken?.access_token;
    const isFirstTokenSync: boolean = !this.hasSyncedAwpCookie;
    const tokenChanged: boolean = previousAccessToken !== currentAccessToken;

    try {
      if (isFirstTokenSync) {
        console.debug(this.logPrefix() + 'Running initial awp_cookie synchronization.');
        await this.awpCookieService.createCookie(currentAccessToken);
      } else if (tokenChanged) {
        console.debug(this.logPrefix() + 'Detected OIDC access token change; refreshing awp_cookie.');
        await this.awpCookieService.refreshCookie(currentAccessToken);
      } else {
        console.debug(this.logPrefix() + 'Skipping awp_cookie sync because access token did not change.');
        return;
      }
      this.hasSyncedAwpCookie = true;
      console.info(this.logPrefix() + 'awp_cookie synchronization completed successfully.');
    } catch (error: unknown) {
      console.error(this.logPrefix() + 'Failed to synchronize awp_cookie with OIDC token.', error);
    }
  }

  fetchAndStoreCsslToken(userData: UserData): Promise<void> {
    const businessPartnerId: string | null = userData.getBusinessPartner()?.getBusinessPartnerId() ?? null;
    return new Promise<void>((resolve, reject): void => {
      this.getCsslTokenFromBackend(userData)
        .then((csslToken: CsslToken): void => {
          this.publishCsslToken(csslToken, businessPartnerId);
          this.scheduleTimerForCsslTokenRefresh(userData);
          resolve();
        })
        .catch((error: Error): void => {
          this.publishCsslToken(null, businessPartnerId);
          reject(error);
        });
    });
  }

  private getCsslTokenFromBackend(userData: UserData): Promise<CsslToken> {
    this.logIfUserHasManyRoles(userData);
    console.info(this.logPrefix() + 'Requesting CSSL token from backend.');
    const startTimeMillis: number = new Date().getTime();
    return new Promise<CsslToken>((resolve, reject) => {
      const headers: HttpHeaders = this.getCsslHttpHeaders(userData);
      // Retrieve a CSSL token; request will contain current WEN authentication via WenInterceptor
      this.httpGetCsslToken(headers).subscribe({
        next: (response: any): void => {
          const csslToken: CsslToken = CsslToken.create(response.data, userData.getContext());
          const durationSecs: string = ((new Date().getTime() - startTimeMillis) / 1000).toFixed(2);
          if (csslToken) {
            console.info(this.logPrefix()
              + `Successfully retrieved CSSL token from backend in ${durationSecs} seconds.`);
            resolve(csslToken);
          } else {
            const errorMessage = `Failed to retrieve CSSL token from backend in ${durationSecs} seconds.`;
            console.error(this.logPrefix() + errorMessage);
            reject(new Error(errorMessage));
          }
        },
        error: (response: any): void => {
          const errorMessage = `Failed to retrieve CSSL token from backend. Response status: ${response.status}.`;
          console.error(this.logPrefix() + errorMessage);
          reject(new Error(errorMessage));
        }
      });
    });
  }

  private logIfUserHasManyRoles(userData: UserData): void {
    const roleCount: number = userData.getRoles()?.length ?? 0;
    if (roleCount >= 100) {
      console.warn(this.logPrefix()
        + `User has ${roleCount} roles; large role sets can bloat CSSL JWTs and may cause header-size related CSSL issues.`);
    }
  }

  get csslTokenBusinessPartnerId(): string | null {
    return this.csslTokenUserBusinessPartnerId;
  }

  private publishCsslToken(csslToken: CsslToken, businessPartnerId: string | null): void {
    csslToken?.writeToStorage();
    this.csslTokenUserBusinessPartnerId = businessPartnerId;
    this.csslTokenSubject.next(csslToken);
  }

  // Schedules the timer for CSSL token refresh
  private scheduleTimerForCsslTokenRefresh(userData: UserData): void {
    // Clear the existing timer if it exists
    if (this.csslTokenRefreshTimer) {
      clearInterval(this.csslTokenRefreshTimer);
    }

    // Memorize the next check date for the first time
    this.memorizeNextCheckDate(0);

    // Set a new interval for token refresh
    this.csslTokenRefreshTimer = setInterval((): void => {
      this.logIfNextCheckDateWasNotMet();
      this.refreshCsslTokenIfExpired(userData);
      this.memorizeNextCheckDate(this.csslTokenRefreshTimeoutMs);
    }, this.csslTokenRefreshTimeoutMs);
  }

  // Memorize the next check date based on the current time and the timeout
  private memorizeNextCheckDate(timeoutMs: number): void {
    this.csslTokenNextCheckDate = new Date(new Date().getTime() + timeoutMs);
  }

  // Log if the next check date was not met, indicating potential throttling
  private logIfNextCheckDateWasNotMet(): void {
    const actualDate = new Date();
    const delayMs = actualDate.getTime() - this.csslTokenNextCheckDate.getTime();
    if (delayMs > 3 * this.csslTokenRefreshTimeoutMs) {
      console.info(this.logPrefix() + 'Throttling detected. CSSL token check runs less frequently, token may expire.');
    }
  }

  private refreshCsslTokenIfExpired(userData: UserData): void {
    const csslToken = CsslToken.readFromStorage(userData.getContext());
    if (!csslToken.jwt) {
      console.warn(this.logPrefix() + 'Token refresh found no CSSL token in local storage. Is CSSL still alive?');
    } else if (csslToken.isRefreshRequired()) {
      console.info(this.logPrefix() + 'The CSSL token requires a refresh.');
    }
    if (!csslToken.jwt || csslToken.isRefreshRequired()) {
      const businessPartnerId: string | null = userData.getBusinessPartner()?.getBusinessPartnerId() ?? null;
      this.getCsslTokenFromBackend(userData)
        .then(csslToken => {
            this.resetCsslTokenRefreshTimeout(userData);
            this.publishCsslToken(csslToken, businessPartnerId);
          }
        )
        .catch(error => {
          console.error(this.logPrefix() +
            `CSSL token refresh failed. Error status: ${error.statusText}. Removing any expired token from storage.`);
          this.increaseCsslTokenRefreshTimeout(userData);
          this.publishCsslToken(null, businessPartnerId);
          csslToken.deleteFromStorage();
        });
    } else if (environment.deploymentEnvironment === 'DEV_LOCAL' || environment.deploymentEnvironment === 'DEV_TEST') {
      console.debug(this.logPrefix() + 'CSSL token is still fresh.');
    }
  }

  private resetCsslTokenRefreshTimeout(userData: UserData): void {
    this.applyCsslTokenRefreshTimeout(2000, userData);
  }

  private increaseCsslTokenRefreshTimeout(userData: UserData): void {
    let timeout = 60000;
    if (this.csslTokenRefreshTimeoutMs < 15000) {
      timeout = 15000;
    } else if (this.csslTokenRefreshTimeoutMs < 30000) {
      timeout = 30000;
    }
    this.applyCsslTokenRefreshTimeout(timeout, userData);
  }

  private applyCsslTokenRefreshTimeout(timeoutMs: number, userData: UserData): void {
    if (this.csslTokenRefreshTimeoutMs != timeoutMs) {
      this.csslTokenRefreshTimeoutMs = timeoutMs;
      console.info(this.logPrefix() + `Setting CSSL refresh timeout to ${this.csslTokenRefreshTimeoutMs}ms.`);
      this.scheduleTimerForCsslTokenRefresh(userData);
    }
  }

  private httpGetCsslToken(headers: HttpHeaders): Observable<any> {
    return this.httpClient.get('/api/token/cssl-jwt/v1', {headers});
  }

  getCsslHttpHeaders(user: UserData): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.set('Auth-App-Id', 'awp');
    headers = headers.set('Auth-Cache-Control', 'no-cache'); // Tell gateway not to return a cached but a fresh token
    headers = headers.set('Auth-Dist-Partner', user.getBusinessPartner().getDistributionPartnerNumber());
    headers = headers.set('Auth-Outlet', user.getBusinessPartner().getOutletNumber());
    return headers;
  }

  private logPrefix(): string {
    return Util.logPrefix('Webapp', 'Auth');
  }
}
